// Import React library
import React from 'react';
// Import Link component for navigation between pages
import { Link } from 'react-router-dom';
// Import authentication hooks to get current user info and permissions
import { useAuth, usePermissions } from '../contexts/AuthContext';
// Import the common layout wrapper that includes header, sidebar, etc.
import Layout from '../components/Layout';
// Import various icons for the dashboard actions
import {
  QrCodeIcon,         // QR code scanner icon
  TagIcon,            // RFID tag icon
  ShoppingCartIcon,   // (2026-04-16, Jason Cox) Manual checkout tile
  ArchiveBoxIcon,     // (2026-04-24, Jason Cox) Inventory tile
  SignalIcon,         // (2026-04-22, Jason Cox) Device status tile
  DocumentTextIcon,   // (2026-04-22, Jason Cox) Consumption Events tile
  TruckIcon,          // (2026-04-23, Jason Cox) Restock Events tile
} from '@heroicons/react/24/outline';

/**
 * Dashboard Component
 * This is the main home screen users see after logging in

 * The dashboard is personalized - different users see different options based on their role
 */
const Dashboard: React.FC = () => {
  // Get the current logged-in user information
  const { user } = useAuth();
  // Get what this user is allowed to do (based on their role)
  const permissions = usePermissions();

  // Define the quick action buttons that can appear on the dashboard
  // Each action is a clickable card that takes user to a different page
  const quickActions = [
    {
      name: 'Tag Items',                          // Display name on the button
      description: 'Match RFID tags to products', // Subtitle explaining what it does
      href: '/tag-items',                         // Which page to go to when clicked
      icon: QrCodeIcon,                          // Which icon to show
      color: 'bg-orange-400',                    // Background color for the icon
      show: permissions.canMatchTags,            // Only show if user has permission
    },
    {
      name: 'Manage Tags',
      description: 'Manage RFID tag details',
      href: '/manage-tags',
      icon: TagIcon,
      color: 'bg-orange-400',
      show: permissions.canManageTags,           // Only show if user has permission
    },
    // (2026-04-16, Jason Cox) Manual Checkout tile (Smart-only, hand scanner).
    {
      name: 'Manual Checkout',
      description: 'Check out tags with R6 hand scanner',
      href: '/manual-checkout',
      icon: ShoppingCartIcon,
      color: 'bg-orange-400',
      // Only Smart users with canManualCheckout see this tile. SWS users are explicitly blocked.
      // (2026-08-10) Also hidden for vendor accounts (companyType === 2), matching the nav
      // item in Layout.tsx. Without this the tile stayed visible for vendors and clicking it
      // bounced straight back off ManualCheckout's guard, reading as a dead tile.
      show:
        user?.system === 'smart' &&
        (permissions.canManualCheckout ?? false) &&
        user?.companyType !== 2,
    },
    // (2026-04-24, Jason Cox) Inventory tile (Smart-only, reads
    // md/v1/listBaseRfid aggregated client-side by SKU).
    {
      name: 'Inventory',
      description: 'On-hand items per SmartBox, aggregated by SKU',
      href: '/inventory',
      icon: ArchiveBoxIcon,
      color: 'bg-orange-400',
      show: user?.system === 'smart' && (permissions.canViewInventory ?? false),
    },
    // (2026-04-22, Jason Cox) Device Status tile (Smart-only, read-only view
    // of md/v1/listSmartBox with a traffic-light layout).
    {
      name: 'Device Status',
      description: 'See which SmartBoxes are online, offline, or sending empty packets',
      href: '/device-status',
      icon: SignalIcon,
      color: 'bg-orange-400',
      show: user?.system === 'smart' && (permissions.canViewDeviceStatus ?? false),
    },
    // (2026-04-22, Jason Cox) Consumption Events tile -- delivery tickets
    // (items consumed out of smartboxes).
    {
      name: 'Consumption Events',
      description: 'Delivery-ticket history with spend summary',
      href: '/delivery-tickets',
      icon: DocumentTextIcon,
      color: 'bg-orange-400',
      show: user?.system === 'smart' && (permissions.canViewDeliveryTickets ?? false),
    },
    // (2026-04-23, Jason Cox) Restock Events tile -- transfer tickets
    // (items restocked INTO smartboxes).
    {
      name: 'Restock Events',
      description: 'Transfer-ticket history with total-value summary',
      href: '/restock-events',
      icon: TruckIcon,
      color: 'bg-orange-400',
      show: user?.system === 'smart' && (permissions.canViewRestockEvents ?? false),
    },
  ]
    .filter(action => action.show)               // Remove actions user can't access
    // (2026-04-23, Jason Cox) Alternate tile icon background between Rebound
    // orange and blue based on the VISIBLE index -- so the pattern holds
    // regardless of which tiles a given user's permissions include.
    .map((action, idx) => ({
      ...action,
      color: idx % 2 === 0 ? 'bg-orange-400' : 'bg-primary-600',
    }));

  // Return the JSX (what shows on screen)
  return (
    // Wrap everything in the Layout component (adds header, sidebar, etc.)
    <Layout>
      {/* Main content area with padding */}
      <div className="py-6">
        {/* Quick Actions section (welcome message now lives in the top header bar) */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-8">
          <div className="mt-8">
            {/* Section title */}
            {/* Grid of action cards - responsive: 1 column on mobile, 2 on tablet, 3 on desktop */}
            <div className="mt-4 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {/* Loop through each allowed action and create a card */}
              {quickActions.map((action) => {
                // Get the icon component for this action
                const Icon = action.icon;
                return (
                  // Each card is a clickable Link that navigates to a different page
                  <Link
                    key={action.name}                    // Unique key for React
                    to={action.href}                     // Where to navigate when clicked
                    className="card hover:shadow-lg transition-shadow group" // Styling classes
                  >
                    <div className="card-body">
                      <div className="flex items-center">
                        {/* Icon area with colored background */}
                        <div className={`flex-shrink-0 p-3 rounded-lg ${action.color}`}>
                          <Icon className="h-6 w-6 text-white" />
                        </div>
                        {/* Compact tile -- (2026-04-30) dropped the description
                            sub-line; the tile name + icon is enough for the
                            grid to read at a glance, and removing the second
                            line halves vertical space on every tile. */}
                        <div className="ml-4 flex-1">
                          <h4 className="text-sm font-medium text-secondary-900 group-hover:text-primary-600">
                            {action.name}
                          </h4>
                        </div>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </div>

        {/* (2026-04-24, Jason Cox) Rebound logo footer -- sits below the
            tile grid. Same asset the sidebar uses. */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-12 mb-4 flex justify-center">
          <img
            src={`${process.env.PUBLIC_URL}/Rebound_Logo.png`}
            alt="Rebound Dynamics"
            className="h-20 w-auto opacity-90"
          />
        </div>
      </div>
    </Layout>
  );
};

// Export so other files can import and use this component
export default Dashboard;